import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { ActivatedRoute } from '@angular/router';
import {
  AppliedCreditType,
  BillingStrategy,
  ListPurchaseFilter,
  Purchase,
  PurchaseItem,
  PurchaseService,
  PurchaseStatus,
  PurchaseStatusEnum,
  billingStrategyToAPI,
  isBillingStrategyInstant,
} from '@galaxy/billing';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import {
  CheckboxFilterControl,
  DateFilterControl,
  FilterGroup,
  NumberInputFilterControl,
  SearchFilterControl,
  SelectFilterControl,
} from '@vendasta/va-filter2';
import { BehaviorSubject, Observable, Subscription, combineLatest, of } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  map,
  skip,
  startWith,
  take,
  withLatestFrom,
} from 'rxjs/operators';
import { detailExpand } from '../../shared/animations';
import { PricePipe } from '../../shared/pipes';
import { billingStrategyToDisplayString, purchaseStatusToDisplayString } from '../../shared/to-string-converstions';
import { Action, DunningDialogComponent } from '../dunning-dialog/dunning-dialog.component';
import { PurchaseItemRow } from '../interface';
import { PurchaseItemRowService } from '../purchase-item-row.service';
import { PurchaseDataSourceService } from '../purchase-list.datasource';
import { RetryPurchaseDialogComponent } from '../retry-purchase-dialog/retry-purchase-dialog.component';
import { VoidPurchaseDialogComponent } from '../void-purchase-dialog/void-purchase-dialog.component';

interface PurchasesConfig {
  id: string;
  name: string;
}

const PURCHASES_CONFIG: PurchasesConfig[] = [
  {
    id: 'timestamp',
    name: 'Timestamp',
  },
  {
    id: 'purchaseId',
    name: 'Purchase ID',
  },
  {
    id: 'status',
    name: 'Status',
  },
  {
    id: 'numberOfItems',
    name: 'Number of Items',
  },
  {
    id: 'total',
    name: 'Total',
  },
  {
    id: 'actions',
    name: 'Actions',
  },
];

const ADMIN_ONLY_COLUMNS: PurchasesConfig[] = [
  {
    id: 'billingStrategy',
    name: 'Billing Strategy',
  },
  {
    id: 'dunningAttempt',
    name: 'Dunning Attempt',
  },
  {
    id: 'pauseDunningUntil',
    name: 'Dunning Paused Until',
  },
];

const PURCHASE_ITEMS_CONFIG: PurchasesConfig[] = [
  {
    id: 'name',
    name: 'Name',
  },
  {
    id: 'account',
    name: 'Account',
  },
  {
    id: 'amount',
    name: 'Amount',
  },
  {
    id: 'discount',
    name: 'Discount',
  },
  {
    id: 'total',
    name: 'Total',
  },
];

@Component({
  selector: 'billing-ui-purchase-list',
  templateUrl: './purchase-list.component.html',
  styleUrls: ['purchase-list.component.scss', '../../shared/shared-styles.scss'],
  animations: [detailExpand],
  providers: [PurchaseItemRowService, PurchaseDataSourceService],
  standalone: false,
})
export class PurchaseListComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges {
  @Input() displayForAdmin = false;
  @Input() allowedStrategies: BillingStrategy[];
  @Input() merchantId: string;
  @Output() accountSelected = new EventEmitter<PurchaseItem>();

  filterGroup: FilterGroup;
  private subscriptions: Subscription[] = [];
  private purchaseFilters$: Observable<ListPurchaseFilter>;
  purchaseItemsCache = new Map<string, Observable<PurchaseItemRow[]>>();
  public exportLoading = new BehaviorSubject(false);

  public length$: Observable<number>;
  public reloadTable$$ = new BehaviorSubject(true);

  public displayColumns: string[];
  public displayColumnsNames: string[];
  public purchaseItemsDisplayColumns: string[];
  statuses = ['All', 'Pending', 'Due', 'Succeeded', 'Overdue', 'Failed'];
  // will display setupFee column if values set

  @ViewChild('purchasePaginator', { static: true }) paginator: MatPaginator;

  constructor(
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private purchaseItemRowService: PurchaseItemRowService,
    public dataSource: PurchaseDataSourceService,
    private pricePipe: PricePipe,
    private purchaseService: PurchaseService,
    private alertService: SnackbarService,
  ) {
    this.filterGroup = new FilterGroup('purchase-list')
      .addToolbarSection(new SearchFilterControl('purchaseId', 'Purchase ID'))
      .addSection('Payment', [
        new SelectFilterControl('status', 'Payment Status', this.paymentStatusOptions),
        new NumberInputFilterControl('minPrice', 'Minimum Amount', '$', ''),
        new CheckboxFilterControl('includeVoided', 'Include Voided', false, {
          appliedValueMapper: (name) => ({ name, label: `Include Voided` }),
        }),
      ])
      .addSection('Period', [new DateFilterControl('start'), new DateFilterControl('end')]);
  }

  get paymentStatusOptions(): Observable<Map<string, string>> {
    const statusMap = new Map<string, string>();
    this.statuses.forEach((s) => statusMap.set(s, s));
    return of(statusMap);
  }

  getFilterValues(): ListPurchaseFilter {
    const { minPrice, status, dunningPaused, ...filterValues } = this.filterGroup.value;
    return {
      ...filterValues,
      merchantId: this.merchantId,
      status: this.statusToValue(status),
      minPrice: Math.floor(minPrice * 100), // convert to cents
      billingStrategyFilter:
        (this.allowedStrategies || []).length > 0
          ? {
              billingStrategy: this.allowedStrategies.map((strategy) => billingStrategyToAPI(strategy)),
            }
          : null,
      dunningPauseStart: dunningPaused ? new Date() : null,
    };
  }

  statusToValue(status: string): number {
    switch (status) {
      case 'Pending':
        return PurchaseStatusEnum.PURCHASE_STATUS_PENDING;
      case 'Due':
        return PurchaseStatusEnum.PURCHASE_STATUS_DUE;
      case 'Succeeded':
        return PurchaseStatusEnum.PURCHASE_STATUS_SUCCESS;
      case 'Overdue':
        return PurchaseStatusEnum.PURCHASE_STATUS_DUNNING;
      case 'Failed':
        return PurchaseStatusEnum.PURCHASE_STATUS_FAILED;
      default:
        return null;
    }
  }

  ngOnInit(): void {
    const columns = PURCHASES_CONFIG;
    if (this.displayForAdmin) {
      columns.splice(3, 0, ...ADMIN_ONLY_COLUMNS);
      this.filterGroup.addSection('Dunning', [
        new CheckboxFilterControl('dunningPaused', 'Only show purchases with dunning paused', false, {
          appliedValueMapper: (name) => ({ name, label: `Dunning paused` }),
        }),
      ]);
    }

    this.displayColumns = columns.map((c) => c.id);
    this.displayColumnsNames = columns.map((c) => c.name);
    this.purchaseItemsDisplayColumns = PURCHASE_ITEMS_CONFIG.map((p) => p.id);

    this.purchaseFilters$ = this.filterGroup.valueChanges.pipe(
      startWith(this.filterGroup.value),
      debounceTime(300),
      distinctUntilChanged(),
      map(() => this.getFilterValues()),
    );

    // TODO - update to let va-filter2 handle query params once it is supported
    this.filterGroup.patchValue({
      purchaseId: this.route.snapshot.queryParamMap.get('purchaseId') || null,
      minimumAmount: this.route.snapshot.queryParamMap.get('minimumAmount') || 1.0,
      state: this.route.snapshot.queryParamMap.get('status') || null,
      includeVoided: this.route.snapshot.queryParamMap.get('includeVoided') || this.displayForAdmin,
      dunningPaused: this.route.snapshot.queryParamMap.get('dunningPaused') || null,
    });

    this.subscriptions.push(
      combineLatest([this.purchaseFilters$, this.reloadTable$$])
        .pipe(skip(1))
        .subscribe(([filter]) => {
          this.paginator.pageIndex = 0; // reset paginator if filters change
          this.dataSource.reloadPurchases(filter, this.paginator.pageSize, this.paginator.pageIndex);
        }),
    );

    this.subscriptions.push(
      this.paginator.page.pipe(withLatestFrom(this.purchaseFilters$)).subscribe(([, filter]) => {
        this.dataSource.updatePage(filter, this.paginator.pageSize, this.paginator.pageIndex);
      }),
    );

    this.length$ = this.dataSource.storedPurchases$.pipe(
      skip(1),
      withLatestFrom(this.dataSource.hasMore$),
      map(([items, hasMore]) => (hasMore ? items.length + this.paginator.pageSize : items.length)),
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.merchantId.previousValue !== changes.merchantId.currentValue) {
      this.filterGroup.updateValueAndValidity({ emitEvent: true });
    }
  }

  ngAfterViewInit(): void {
    this.purchaseFilters$
      .pipe(take(1))
      .subscribe((filter) => this.dataSource.loadPurchases(filter, this.paginator.pageSize, this.paginator.pageIndex));
  }

  getPurchaseItemRows(purchase: Purchase): Observable<PurchaseItemRow[]> {
    // It is important the reference to the observable is the same for all unique calls to prevent unnecessary requests
    if (this.purchaseItemsCache.has(purchase.purchaseId)) {
      return this.purchaseItemsCache.get(purchase.purchaseId);
    }
    const purchaseItems$ = this.purchaseItemRowService.buildItemRows(purchase.items);
    this.purchaseItemsCache.set(purchase.purchaseId, purchaseItems$);
    return purchaseItems$;
  }

  openRetryDialog(purchase: Purchase): void {
    this.dialog
      .open(RetryPurchaseDialogComponent, {
        width: '275px',
        data: {
          purchase: purchase,
        },
      } as MatDialogConfig)
      .afterClosed()
      .subscribe((res) => {
        if (res) this.reloadTable$$.next(true);
      });
  }

  openVoidDialog(purchase: Purchase): void {
    this.dialog
      .open(VoidPurchaseDialogComponent, {
        width: '500px',
        data: {
          purchase: purchase,
        },
      } as MatDialogConfig)
      .afterClosed()
      .subscribe((res) => {
        if (res) this.reloadTable$$.next(true);
      });
  }

  openDunningDialog(purchase: Purchase, action: Action): void {
    this.dialog
      .open(DunningDialogComponent, {
        width: '500px',
        data: {
          action: action,
          purchase: purchase,
        },
      } as MatDialogConfig)
      .afterClosed()
      .subscribe((res) => {
        if (res) this.reloadTable$$.next(true);
      });
  }

  /**
   * Ensure that we are always passing a Date object.
   * The SDK we are using is expecting a Date object type parameter.
   * @param filterDate
   */
  convertStrToDateObj(filterDate): Date {
    if (typeof filterDate === 'string') {
      return new Date(filterDate);
    }
    return filterDate;
  }

  exportList(): void {
    this.exportLoading.next(true);
    const { start, end, ...filters } = this.getFilterValues();
    this.purchaseService
      .exportCSV({
        ...filters,
        start: this.convertStrToDateObj(start),
        end: this.convertStrToDateObj(end),
      })
      .pipe(
        catchError(() => {
          this.alertService.openErrorSnack('failed to export purchases');
          return of(null);
        }),
        take(1),
      )
      .subscribe(() => {
        this.alertService.openSuccessSnack("Your CSV is being prepared, you will be notified when it's ready");
        this.exportLoading.next(false);
      });
  }

  navigateToAccount(purchaseItem: PurchaseItem): void {
    this.accountSelected.emit(purchaseItem);
  }

  billingStrategyToDisplay(bs: BillingStrategy): string {
    return billingStrategyToDisplayString(bs);
  }

  purchaseStatusToDisplay(ps: PurchaseStatus): string {
    return purchaseStatusToDisplayString(ps);
  }

  isVoided(purchase: Purchase): boolean {
    return !(!purchase || purchase.voided == null);
  }

  hasDiscount(purchase: Purchase): boolean {
    if (!purchase || purchase.items.length <= 0) {
      return false;
    }
    return purchase.items.some((pi) => pi.discountAmount > 0);
  }

  hasCreditAmount(purchase: Purchase): boolean {
    return purchase.creditAmount > 0;
  }

  displayCreditAmount(purchase: Purchase): number {
    return purchase.creditAmount;
  }

  promotionalCreditAmount(purchase: Purchase): number {
    return purchase.appliedCredits.reduce<number>(
      (sum, ap) => (ap.referenceType === AppliedCreditType.CREDIT_TYPE_PROMOTIONAL ? sum + ap.appliedAmount : sum),
      0,
    );
  }

  creditAmount(purchase: Purchase): number {
    return purchase.appliedCredits.reduce<number>(
      (sum, ap) => (ap.referenceType === AppliedCreditType.CREDIT_TYPE_CREDIT_NOTE ? sum + ap.appliedAmount : sum),
      0,
    );
  }

  creditCardPaymentAmount(purchase: Purchase): number {
    return purchase.paid ? purchase.total - purchase.creditAmount : 0;
  }

  formatPaymentTotalTooltip(purchase: Purchase): string {
    let tooltip = this.pricePipe.transform(
      this.creditCardPaymentAmount(purchase),
      purchase.currency,
      null,
      true,
      false,
      true,
    );
    const vCash = this.promotionalCreditAmount(purchase);
    const creditNotes = this.creditAmount(purchase);
    if (vCash > 0) {
      const vCashFormatted = this.pricePipe.transform(vCash, purchase.currency, null, true, false, true);
      tooltip = `vCash ${vCashFormatted} + ${tooltip}`;
    }
    if (creditNotes > 0) {
      const creditNotesFormatted = this.pricePipe.transform(creditNotes, purchase.currency, null, true, false, true);
      tooltip = `Credit ${creditNotesFormatted} + ${tooltip}`;
    }
    return tooltip;
  }

  isInstant(purchase: Purchase): boolean {
    return isBillingStrategyInstant(purchase.billingStrategy);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }
}
